import { LoadingOutlined } from "@ant-design/icons";
import { isEmbroideryTrace } from "../../../config/appVariant";
import { IonLoading, IonIcon } from "@ionic/react";
import { brushOutline, sparklesOutline } from 'ionicons/icons';
import { Button, Col, Form, Input, Row, notification } from "antd";
import axios from "axios";
import Filter from "bad-words"; // Import Filter from bad-words package
import { useContext, useState } from "react";
import { useHistory } from "react-router-dom";
import focusedSuffixIcon from "../../../assets/left-active-arrow.svg";
import defaultSuffixIcon from "../../../assets/left-arrow.svg";
import svgTraceIcon from "../../../assets/svgtrace.svg";
import AppContext from "../../../contexts/AppContext";
import { useAuth } from "../../../contexts/AuthContext";
import { storePendingFileId } from "../../../lib/firebase/pendingFiles";
import { trackIntercomEvent } from "../../../lib/intercom/intercom";
import "./DallePromptForm.css";
import GetCredits from "./GetCredits/GetCredits";

const { useNotification } = notification;

const loadingMessage = "Generating your design! Please wait...";

const DallePromptForm = ({ isModalVisible, setIsModalVisible }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [getCreditModal, setGetCreditModal] = useState(false);

  const { setImage64, setImageSource } = useContext(AppContext);
  const { setImageSEO } = useContext(AppContext);
  const { setImagePrompt } = useContext(AppContext);
  const { guestMode, pendingFileId } = useContext(AppContext);
  const [api, contextHolder] = useNotification();
  const { user } = useAuth();
  const history = useHistory();
  const [isTyping, setIsTyping] = useState(false);
  const [isFocused, setIsFocused] = useState(false);

  const filter = new Filter();

  const checkPromptForProfanity = async (prompt) => {
    if (filter.isProfane(prompt)) {
      console.log("Profanity detected in prompt. Please revise.");
    }
    return true;
  };

  const openNotification = (message, type) => {
    api.open({
      message,
      duration: 3,
      type,
      placement: "top",
    });
  };

  const generateImage = async (prompt) => {
    setLoading(true);
    if (!(await checkPromptForProfanity(prompt))) {
      setLoading(false);
      // setIsModalVisible(false); // Hide modal if there's an error
      openNotification(
        "Profanity detected in prompt. Please revise before sending.",
        "error"
      );
      return;
    }
    try {
      let resp;
      
      if (guestMode && !user) {
        // Use guest endpoint - no auth required
        const apiUrl = 'https://us-central1-svgtrace-pro.cloudfunctions.net/generateImageGrokGuest';
        resp = await axios.post(
          apiUrl,
          { prompt, pendingFileId },
          {
            headers: {
              "Content-Type": "application/json",
            },
            timeout: 120000, // 2 minute timeout
          }
        );
      } else {
        // Use authenticated endpoint
        if (!user) {
          openNotification("Please sign in to generate custom designs.", "error");
          setLoading(false);
          return;
        }
        const accessToken = await user.getIdToken();
        
        // Use Grok (xAI) for image generation
        const apiUrl = 'https://us-central1-svgtrace-pro.cloudfunctions.net/generateImageGrok';
        
        resp = await axios.post(
          apiUrl,
          { prompt },
          {
            headers: {
              Authorization: `Bearer ${accessToken}`,
              "Content-Type": "application/json",
            },
            timeout: 120000, // 2 minute timeout
          }
        );
      }
      setImageSEO({
        title: resp.data.title,
        description: resp.data.seoDescription,
      });
      
      // For guest mode: redirect to converter with fileId (similar to file upload flow)
      if (guestMode && !user && resp.data.fileId) {
        // Store pending file ID for retrieval after signup
        storePendingFileId(resp.data.fileId);
        
        // Redirect to guest converter
        history.push(`/convert?file=${resp.data.fileId}`);
        setIsModalVisible && setIsModalVisible(false);
        return;
      }
      
      // For authenticated users: set image and open converter normally
      setImage64("data:image/png;base64," + resp.data.base64);
      setImageSource("generated");
      trackIntercomEvent('used_ai_generator', { prompt: prompt.substring(0, 100) });
      openNotification("Image generated successfully", "success");
      setIsModalVisible && setIsModalVisible(false); // Close modal on success
      
      // Trigger tutorial action if tutorial is active
      if (window.tutorialTriggerAction) {
        window.tutorialTriggerAction('file-uploaded', {
          fileName: 'generated-image.png',
          fileType: 'image/png',
          source: 'generated'
        });
      }
    } catch (error) {
      console.error(error);
      if (error.response?.status === 402) {
        openNotification("You've reached your generation limit. Please upgrade to continue.", "error");
      } else {
        openNotification("Something went wrong while generating the image.", "error");
      }
    }
    setLoading(false);
    // setLoadingMessage(`Generating your design! Please wait...${progress}%`);
    // setIsModalVisible(false); // Hide modal when generation is complete
  };



  const onFinish = (values) => {
    const { subject } = values;

    setImagePrompt(subject);
    const prompt = `Create a flat, vector-style image on a solid background. The design should feature a ${subject}, keep it simple and clean for easy cutting. The image should have a clear and solid background, without any additional elements. Use the SVG image previews off etsy as a reference.`;

    generateImage(subject);
  };



  const handleFocus = () => {
    setIsFocused(true);
  };

  const handleInput = (e) => {
    setIsTyping(e.target.value.length > 0);
  };

  const handleBlur = () => {
    setIsFocused(false);
    setIsTyping(false);
  };

  const inputClassName = `input-field ${isFocused && "focused"} ${
    isTyping && "typing"
  }`;
  // Determine which icon to use based on the state
  let suffixIconSrc = defaultSuffixIcon;
  if (isFocused) {
    suffixIconSrc = focusedSuffixIcon;
  }
  if (isTyping) {
    suffixIconSrc = focusedSuffixIcon;
  }

  return (
    <>
      {contextHolder}

      <IonLoading
        id="generateLoading"
        isOpen={loading}
        message={loadingMessage}
        // className="modal-blur-background"
      />
      <div
        style={{
          padding: "20px 20px 16px 20px",
          margin: "0",
          position: "relative",
          width: "100%",
          height: "auto",
          background: "linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)",
          display: "flex",
          flexDirection: "column",
          borderRadius: "12px",
          boxShadow: "0 8px 32px rgba(30, 60, 114, 0.3)",
          border: "1px solid rgba(255, 255, 255, 0.1)",
          overflow: "hidden",
        }}
      >
        {/* Subtle background overlay */}
        <div
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: "linear-gradient(135deg, rgba(255, 255, 255, 0.05) 0%, rgba(255, 255, 255, 0.02) 100%)",
            pointerEvents: "none",
          }}
        />

        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "10px",
            marginBottom: "24px",
            width: "100%",
            position: "relative",
            zIndex: 1,
          }}
        >
          <IonIcon icon={brushOutline} style={{ 
            fontSize: "28px", 
            color: "#ffffff",
            marginRight: "8px"
          }} />
          <h1 className="generate-image-title">
            {loading ? "Creating your design..." : isEmbroideryTrace ? "Generate an Embroidery Design" : "Generate an image"}
          </h1>
          <Button
            type="primary"
            size="small"
            onClick={() => {
              /* Define your button action here */
            }}
            style={{
              borderRadius: "12px",
              background: "linear-gradient(105deg, #00E9FF -71.56%, #FD90FF 131.26%)",
              color: "#000",
              fontWeight: "600",
              border: "none",
              padding: "4px 12px",
              fontSize: "11px",
              letterSpacing: "0.5px",
            }}
          >
            PRO
          </Button>
        </div>

        <div style={{
          textAlign: "center",
          marginBottom: "32px",
          position: "relative",
          zIndex: 1,
        }}>
          <p style={{
            fontSize: "16px",
            fontWeight: "400",
            color: "rgba(255, 255, 255, 0.9)",
            margin: "0 0 16px 0",
            lineHeight: "1.5",
          }}>
            {isEmbroideryTrace ? "Describe your design and we'll generate it ready for embroidery" : "Describe your design idea and we'll create it for you"}
          </p>
          <p style={{
            fontSize: "13px",
            fontWeight: "400",
            fontStyle: "italic",
            color: "rgba(255, 255, 255, 0.7)",
            margin: 0,
          }}>
            {isEmbroideryTrace ? "Optimized for embroidery & stitch conversion" : "Optimized for Cricut cutting & SVG conversion"}
          </p>
        </div>



        <div style={{ 
          display: "flex", 
          alignItems: "center", 
          justifyContent: "center",
          flex: 1,
          position: "relative",
          zIndex: 1,
        }}>
          <Row align="middle" justify="center" style={{ width: "100%" }}>
            <Col span={20}>
              <Form
                form={form}
                onFinish={onFinish}
                layout="vertical"
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                }}
              >
                <Form.Item
                  name="subject"
                  rules={[
                    { required: true, message: "Please describe your design." },
                  ]}
                  style={{ width: "100%", fontWeight: "400", marginBottom: "20px" }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      width: "100%",
                      position: "relative",
                    }}
                  >
                    <Input
                      loading={loading.toString()}
                      disabled={loading}
                      size="large"
                      placeholder={isEmbroideryTrace ? "Example: A floral wreath with clean outlines for embroidery" : "Example: A minimalist cat with a bowtie for a logo"}
                      className={inputClassName}
                      onFocus={handleFocus}
                      onInput={handleInput}
                      onBlur={handleBlur}
                      style={{
                        borderRadius: "12px",
                        border: "2px solid rgba(255, 255, 255, 0.2)",
                        background: "rgba(255, 255, 255, 0.12)",
                        backdropFilter: "blur(15px)",
                        color: "#ffffff",
                        fontSize: "16px",
                        padding: "16px 20px",
                        transition: "all 0.3s ease",
                        height: "56px",
                      }}
                      suffix={
                        <Button
                          disabled={loading}
                          htmlType="submit"
                          style={{
                            border: "none",
                            outline: "none",
                            backgroundColor: "rgba(255, 255, 255, 0.15)",
                            borderRadius: "8px",
                            padding: "8px 12px",
                            transition: "all 0.2s ease",
                          }}
                          icon={
                            loading ? (
                              <LoadingOutlined style={{ color: "#ffffff" }} />
                            ) : (
                              <Row>
                                <img
                                  src={suffixIconSrc}
                                  alt="Generate"
                                  style={{ width: "20px", height: "20px" }}
                                />
                              </Row>
                            )
                          }
                        />
                      }
                    />
                  </div>
                </Form.Item>
              </Form>
            </Col>
          </Row>
        </div>

        <div style={{
          display: "flex",
          justifyContent: "center",
          gap: "12px",
          marginTop: "24px",
          flexWrap: "wrap",
          position: "relative",
          zIndex: 1,
        }}>
          <div style={{
            background: "rgba(255, 255, 255, 0.12)",
            color: "#ffffff",
            padding: "8px 16px",
            borderRadius: "20px",
            fontSize: "12px",
            fontWeight: "600",
            border: "1px solid rgba(255, 255, 255, 0.15)",
            backdropFilter: "blur(10px)",
            display: "flex",
            alignItems: "center",
            gap: "6px",
          }}>
            <span style={{ fontSize: "14px" }}>{isEmbroideryTrace ? "🪡" : "✂️"}</span>
            <span>{isEmbroideryTrace ? "Stitch Ready" : "Cricut Ready"}</span>
          </div>
          <div style={{
            background: "rgba(255, 255, 255, 0.12)",
            color: "#ffffff",
            padding: "8px 16px",
            borderRadius: "20px",
            fontSize: "12px",
            fontWeight: "600",
            border: "1px solid rgba(255, 255, 255, 0.15)",
            backdropFilter: "blur(10px)",
            display: "flex",
            alignItems: "center",
            gap: "6px",
          }}>
            <span style={{ fontSize: "14px" }}>{isEmbroideryTrace ? "🎨" : "🎨"}</span>
            <span>{isEmbroideryTrace ? "Clean Outlines" : "Solid Colors"}</span>
          </div>
          <div style={{
            background: "rgba(255, 255, 255, 0.12)",
            color: "#ffffff",
            padding: "8px 16px",
            borderRadius: "20px",
            fontSize: "12px",
            fontWeight: "600",
            border: "1px solid rgba(255, 255, 255, 0.15)",
            backdropFilter: "blur(10px)",
            display: "flex",
            alignItems: "center",
            gap: "6px",
          }}>
            <span style={{ fontSize: "14px" }}>{isEmbroideryTrace ? "✨" : "⭐"}</span>
            <span>{isEmbroideryTrace ? "DST & PES Ready" : "Easy to Weed"}</span>
          </div>
        </div>
      </div>
      {getCreditModal && <GetCredits setGetCreditModal={setGetCreditModal} />}
    </>
  );
};

export default DallePromptForm;
